/**
 * Configuration by process (Business Opportunities): the tab's AI processes as
 * rows (name and assigned model, or "No model"), the selected one's name, description and
 * parts as tabs beside them. The process is in `&process=<key>` and the open part in
 * `&part=<part>` (both replace), so each links. An unknown process or a part the caller
 * may not see opens the first allowed one; with no allowed part the detail says so.
 * The processes and models are read once for the tab, not once per row.
 */

import type { ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useModels, useProcesses } from '@/api/queries';
import type { AiModel } from '@/api/types';
import { useSession } from '@/session/SessionContext';
import type { PermissionKey } from '@/session/permissions';
import { ListDetail, ListDetailRow } from '@/ui/ListDetail';
import { SectionActionsProvider } from '@/ui/SectionActions';
import { LoadFailure, LoadingCard } from '@/ui/StateCard';
import { Tabs } from '@/ui/Tabs';

export interface ProcessInfo {
  key: string;
  name: string;
  description: string;
  has_prompt: boolean;
  /** Absent when the caller may not read the assignments. */
  model_key?: string | null;
}

export interface ProcessPart {
  key: string;
  label: string;
  /** Without it the part follows the tab's own permission. */
  permission?: PermissionKey;
  render: () => ReactNode;
}

interface Config {
  /** Names the list ("Business Opportunities processes"). */
  label: string;
  /** The tab's processes, in the order they list. */
  keys: readonly string[];
  parts: (process: ProcessInfo) => ProcessPart[];
}

function modelLabel(process: ProcessInfo, models: AiModel[]): string | undefined {
  if (process.model_key === undefined) return undefined;
  if (process.model_key === null) return 'No model';
  return models.find((m) => m.key === process.model_key)?.display_name ?? process.model_key;
}

function ProcessDetail({ process, parts, label }: { process: ProcessInfo; parts: ProcessPart[]; label: string }) {
  const { permissions } = useSession();
  const allowed = parts.filter((p) => !p.permission || permissions.has(p.permission));
  return (
    <SectionActionsProvider>
      {(slotRef) => (
        <div className="stack-md">
          <div className="stack-sm">
            <h2>{process.name}</h2>
            <p className="hint">{process.description}</p>
          </div>
          {allowed.length === 0 ? (
            <p role="status">You do not have access to these settings.</p>
          ) : (
            <Tabs
              tabs={allowed.map(({ key, label: name }) => ({ key, label: name }))}
              label={`${label}: ${process.name}`}
              param="part"
              history="replace"
              resets={['version']}
              trailing={<div ref={slotRef} className="page-actions" />}
            >
              {(part) => allowed.find((p) => p.key === part)?.render()}
            </Tabs>
          )}
        </div>
      )}
    </SectionActionsProvider>
  );
}

function Workbench({ config, processes, models }: { config: Config; processes: ProcessInfo[]; models: AiModel[] }) {
  const [params, setParams] = useSearchParams();
  const current = processes.find((p) => p.key === params.get('process')) ?? processes[0];
  if (!current) return null;

  const select = (key: string) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('process', key);
        next.delete('part');
        next.delete('version');
        return next;
      },
      { replace: true },
    );

  return (
    <ListDetail
      label={config.label}
      list={processes.map((p) => (
        <ListDetailRow
          key={p.key}
          primary={p.name}
          secondary={modelLabel(p, models)}
          title={p.name}
          selected={p.key === current.key}
          onSelect={() => select(p.key)}
        />
      ))}
      detail={<ProcessDetail key={current.key} process={current} parts={config.parts(current)} label={config.label} />}
    />
  );
}

function WithAssignments({ config }: { config: Config }) {
  const models = useModels();
  const processes = useProcesses();
  if (models.isPending || processes.isPending) return <LoadingCard label="Loading processes" />;
  const failure = models.error ?? processes.error;
  if (failure) {
    return (
      <LoadFailure
        error={failure}
        onRetry={() => {
          void models.refetch();
          void processes.refetch();
        }}
      />
    );
  }
  if (!models.data || !processes.data) return null;
  const listed = config.keys.flatMap((key) => processes.data.filter((p) => p.key === key));
  return <Workbench config={config} processes={listed} models={models.data} />;
}

/**
 * `known` describes the processes for a caller who may not read the assignments
 * (`ai.processes.view`): they see the parts they hold, without a model line.
 */
export function ProcessWorkbench({ known, ...config }: Config & { known: ProcessInfo[] }) {
  const canAssignments = useSession().can('ai.processes.view');
  if (!canAssignments) return <Workbench config={config} processes={known} models={[]} />;
  return <WithAssignments config={config} />;
}
